import { RequestStatus } from 'types/RequestStatus';
import {
	Button,
	HStack,
	Input,
	VStack,
	Text,
	Tab,
	TabPanel,
	TabPanels,
	Tabs,
} from '@montel/montelpro-shared-components-v2';
import { useGetOnMount } from 'hooks/useGetOnMount';
import {
	DEVELOPER_API_GET_ACCOUNT_AWAITING_HUBSPOT_SYNC,
	DEVELOPER_API_SYNC_ACCOUNTS_TO_HUBSPOT,
} from 'apiRoutes';
import { accountTableColumns } from 'components/account/accountTableColumns';
import { Table } from 'components/tables/Table';
import { MontelAccount } from 'domain/account/interfaces';
import useSubmit, { POST } from 'hooks/useSubmit';
import { FunctionComponent, useState } from 'react';
import { RequestLoader } from 'components/common/Loader';

interface HubSpotSyncData {
	syncableAccounts: MontelAccount[];
	unsyncableAccounts: MontelAccount[];
}

const HubSpotSyncTool: FunctionComponent = () => {
	const {
		data: usersData,
		status: usersStatus,
		getData: userDataGet,
	} = useGetOnMount<HubSpotSyncData>(
		DEVELOPER_API_GET_ACCOUNT_AWAITING_HUBSPOT_SYNC,
	);

	const columns = [
		accountTableColumns.fullName(),
		accountTableColumns.email(),
		accountTableColumns.jobTitle(),
		accountTableColumns.registeredCompany({
			headCellProps: {
				text: 'Company',
			},
		}),
	];

	const [batchSize, setBatchSize] = useState<number>(50);
	const [submit, status] = useSubmit(
		`${DEVELOPER_API_SYNC_ACCOUNTS_TO_HUBSPOT}`,
		POST,
		{
			messageOnSuccess: 'Accounts synced to HubSpot',
			messageOnError: 'Error syncing accounts',
			showApiError: true,
		},
	);

	const onSubmit = async () => {
		const response = await submit({
			batchSize: batchSize,
		});

		if (response.success) {
			userDataGet();
		}
	};

	const syncableAccounts = usersData?.syncableAccounts ?? [];
	const unsyncableAccounts = usersData?.unsyncableAccounts ?? [];

	const [activeTab, setActiveTab] = useState('syncable');

	return (
		<RequestLoader status={usersStatus}>
			<Text>Sync accounts without a HubSpot ID to HubSpot</Text>
			<HStack wrap="nowrap">
				<Input
					label="Batch size"
					type="number"
					autoComplete="off"
					name="batchSize"
					value={batchSize}
					required
					onChange={(event) => setBatchSize(event.target.value)}
				/>
			</HStack>
			<Button
				onClick={onSubmit}
				type="submit"
				text="Sync"
				loadingText="Syncing accounts ..."
				isLoading={status === RequestStatus.LOADING}
			/>

			{usersData && (
				<VStack gap="lg" fullWidth>
					<Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
						<Tab
							value="syncable"
							label={`Syncable (${syncableAccounts.length})`}
						/>
						<Tab
							value="unsyncable"
							label={`Unsyncable (${unsyncableAccounts.length})`}
						/>
					</Tabs>
					<TabPanels currentTab={activeTab}>
						<TabPanel name="syncable">
							<Table<MontelAccount>
								columns={columns}
								data={syncableAccounts}
								withPagination
								showRowCount
							/>
						</TabPanel>
						<TabPanel name="unsyncable">
							<Table<MontelAccount>
								columns={columns}
								data={unsyncableAccounts}
								withPagination
								showRowCount
							/>
						</TabPanel>
					</TabPanels>
				</VStack>
			)}
		</RequestLoader>
	);
};

export default HubSpotSyncTool;
